import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Spinner, SpinnerSize } from '@fluentui/react';
import { useAuth } from '@auth';
import { hasAnyPermission } from '@auth/permissions';
import { rememberRedirectStartPage } from '@auth/authRedirect';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermissions?: string[];
  redirectTo?: string;
}

/**
 * Requires authentication. When requiredPermissions is set, the user must have
 * at least one of those permission ids from /api/me/permissions.
 */
export function ProtectedRoute({
  children,
  requiredPermissions = [],
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}>
        <Spinner size={SpinnerSize.large} label="Authenticating..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    rememberRedirectStartPage();
    return <Navigate to={redirectTo} replace />;
  }

  if (requiredPermissions.length > 0 && !hasAnyPermission(user, requiredPermissions)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
